import { Button } from "@/components/ui/button";
import { BookOpen, RefreshCw, Lightbulb, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AIModuleCoach from "@/components/ai/AIModuleCoach";

interface RemediationContent {
  title: string;
  concepts: string[];
  tips: string[];
  encouragement: string;
}

const MODULE_REMEDIATION: Record<string, RemediationContent> = {
  account_setup: {
    title: "Account Setup",
    concepts: ["Understanding checking vs savings accounts", "Emergency fund purpose and sizing", "Where your money lives and why it matters"],
    tips: ["Think about what each account type is designed for", "Emergency funds cover unexpected costs, not planned ones", "Separate your money by purpose, not just amount"],
    encouragement: "You're building the foundation — every financial expert started here.",
  },
  budget_builder: {
    title: "Budget Builder",
    concepts: ["The 50/30/20 budgeting framework", "Needs vs wants distinction", "Emergency fund allocation within a budget"],
    tips: ["Start with your actual take-home pay, not gross income", "Be honest about what's a need vs a want", "Adjust your budget after unexpected expenses — that's normal"],
    encouragement: "Budgeting is a skill you build over time. Try again with fresh eyes.",
  },
  credit_management: {
    title: "Credit Management",
    concepts: ["How credit utilization affects your score", "Minimum payments vs full payments", "The long-term cost of carrying a balance"],
    tips: ["Keep utilization below 30% of your limit", "Paying more than the minimum saves you money over time", "On-time payments are the biggest factor in your score"],
    encouragement: "Credit is one of the most powerful financial tools. Understanding it well pays off for life.",
  },
  career_path: {
    title: "Career Path",
    concepts: ["Multiple income streams", "Active vs passive income", "Career progression and earning potential"],
    tips: ["Diversify income sources when possible", "Invest in skills that increase your earning power", "Side income can accelerate financial goals"],
    encouragement: "Your income is your most powerful wealth-building tool. Keep exploring!",
  },
  vehicle_ownership: {
    title: "Vehicle Ownership",
    concepts: ["Total cost of ownership beyond sticker price", "Depreciation and its impact", "Insurance, fuel, and maintenance budgeting"],
    tips: ["Compare monthly ownership cost, not just purchase price", "Consider reliability ratings to avoid surprise repairs", "A cheaper car can be more expensive if it's unreliable"],
    encouragement: "Smart vehicle decisions save thousands over a lifetime. You're learning the right way.",
  },
  risk_protection: {
    title: "Risk Protection",
    concepts: ["Deductible vs premium tradeoffs", "Coverage types and what they protect", "When insurance is worth the cost"],
    tips: ["Higher deductibles = lower premiums, but more out-of-pocket risk", "Don't over-insure or under-insure", "Think of insurance as protection for your financial plan"],
    encouragement: "Risk management is what separates good financial plans from great ones.",
  },
  family_budget: {
    title: "Family Budget & Planning",
    concepts: ["Household expense management", "Planning for dependents", "Balancing family priorities"],
    tips: ["Communicate about money as a household", "Plan for both short-term and long-term family needs", "Build flexibility into your family budget"],
    encouragement: "Financial planning for a family is complex. Each attempt makes you better at it.",
  },
  market_simulator: {
    title: "Market Simulator",
    concepts: ["Diversification across sectors", "Risk vs return tradeoffs", "Emotional discipline in investing"],
    tips: ["Don't put all your money in one stock or sector", "Avoid panic selling during downturns", "Consistent investing beats trying to time the market"],
    encouragement: "Even professional investors make mistakes. The key is learning from each round.",
  },
  tax_strategy: {
    title: "Tax Strategy",
    concepts: ["Tax brackets and marginal rates", "Tax-advantaged account types", "Deductions vs credits"],
    tips: ["Understand how tax brackets actually work — not all income is taxed at the same rate", "Maximize tax-advantaged contributions when possible", "Tax credits are more valuable than deductions"],
    encouragement: "Tax strategy is advanced territory. Getting better here directly increases your wealth.",
  },
  wealth_strategy: {
    title: "Wealth Strategy",
    concepts: ["Asset allocation by account type", "Tax-efficient investing", "Long-term compounding"],
    tips: ["Place tax-inefficient assets in tax-advantaged accounts", "Rebalance periodically to maintain your target allocation", "Time in the market matters more than timing the market"],
    encouragement: "You're tackling the concepts that separate financial literacy from financial mastery.",
  },
  homeownership: {
    title: "Homeownership",
    concepts: ["Affordability beyond mortgage payments", "Down payment impacts", "Closing costs and hidden expenses"],
    tips: ["Use the 28/36 rule for affordability", "Larger down payments reduce long-term costs", "Factor in property taxes, insurance, and maintenance"],
    encouragement: "Home buying is one of the biggest financial decisions. Taking time to learn it well is smart.",
  },
  re_investing: {
    title: "Real Estate Investing",
    concepts: ["Cash flow analysis", "Cap rate and ROI calculations", "Tenant and maintenance risk"],
    tips: ["Positive cash flow is more important than appreciation hopes", "Always budget for vacancy and repairs", "Location and tenant demand drive returns"],
    encouragement: "Real estate investing rewards patience and analysis. Review the deals carefully.",
  },
  development: {
    title: "Development",
    concepts: ["Project cost estimation", "Timeline and budget management", "ROI on development projects"],
    tips: ["Always add contingency to cost estimates", "Delays are common — plan for them", "Market research before breaking ground"],
    encouragement: "Development is high-stakes, high-reward. Better analysis leads to better outcomes.",
  },
  entrepreneurship: {
    title: "Entrepreneurship",
    concepts: ["Cash flow management for businesses", "Pricing strategy", "Growth vs stability tradeoffs"],
    tips: ["Cash is king — watch your burn rate", "Price based on value, not just cost", "Growth too fast without stability can kill a business"],
    encouragement: "Building a business is one of the hardest and most rewarding paths. Keep iterating!",
  },
  recovery_mode: {
    title: "Recovery Mode",
    concepts: ["Emergency financial stabilization", "Prioritizing debts", "Rebuilding after setbacks"],
    tips: ["Focus on essential expenses first", "Negotiate with creditors when possible", "Small consistent steps rebuild faster than big risky moves"],
    encouragement: "Recovery is about resilience. Understanding these strategies protects your future.",
  },
  scam_defense: {
    title: "Scam Defense",
    concepts: ["Common scam patterns", "Social engineering tactics", "Identity protection"],
    tips: ["If it sounds too good to be true, it probably is", "Never share passwords or codes with anyone", "Verify requests through official channels"],
    encouragement: "Knowing how to spot scams is a superpower. You're building financial self-defense.",
  },
};

const DEFAULT_REMEDIATION: RemediationContent = {
  title: "Module",
  concepts: ["Core financial concepts covered in this module", "Decision-making under uncertainty", "Connecting theory to practice"],
  tips: ["Review the related course lessons before retrying", "Pay attention to the feedback after each decision", "Take your time — there's no penalty for thinking carefully"],
  encouragement: "Every attempt teaches you something new. You're getting closer to mastery!",
};

interface Props {
  moduleKey: string;
  score: number;
  maxScore: number;
  onRetry: () => void;
  /** Where the "back" button navigates. Beta modules pass "/dashboard". */
  backHref?: string;
  /** Label for the "back" button. Beta modules pass "Back to Dashboard". */
  backLabel?: string;
}

export default function ModuleRemediationScreen({
  moduleKey,
  score,
  maxScore,
  onRetry,
  backHref = "/dashboard",
  backLabel = "Back to Dashboard",
}: Props) {
  const navigate = useNavigate();
  const content = MODULE_REMEDIATION[moduleKey] || DEFAULT_REMEDIATION;
  const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-lg w-full space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto">
            <BookOpen className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground">
            Keep Going — {pct}% Score
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            You need at least 80% to complete {content.title}. Review the key concepts below and try again when you're ready.
          </p>
        </div>

        {/* Key Concepts Card */}
        <div className="rounded-2xl border border-border bg-card/80 p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-semibold text-sm text-foreground">Key Concepts to Review</h3>
          </div>
          <ul className="space-y-2">
            {content.concepts.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                <span className="text-primary mt-0.5">•</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Tips Card */}
        <div className="rounded-2xl border border-border bg-card/80 p-6 space-y-4">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-accent" />
            <h3 className="font-heading font-semibold text-sm text-foreground">Tips for Next Attempt</h3>
          </div>
          <ul className="space-y-2">
            {content.tips.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                <span className="text-accent mt-0.5">•</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Encouragement */}
        <p className="text-center text-sm text-muted-foreground/80 italic">
          {content.encouragement}
        </p>

        {/* AI Module Coach */}
        <AIModuleCoach
          moduleKey={moduleKey}
          moduleName={content.title}
          score={score}
          maxScore={maxScore}
        />

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <Button onClick={onRetry} className="w-full gap-2">
            Retry Module <ArrowRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" onClick={() => navigate(backHref)} className="w-full">
            {backLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
